// Implements: F-UIX-019
/**
 * Who sees the platform admin's views (UIX.8), read from the server's permission answer on the
 * session's platform scope (UIX.5, S12d), never from a role name.
 *
 * The server publishes three actions on the platform scope today (`scope_permissions.py`): reading
 * the platform totals, managing an organization's ceiling and reading the system status. The
 * fourth, managing the model settings (F-GEN-036), is published by the server once MS-07 lands;
 * until then no session holds it and the section stays hidden. Each one
 * opens its own section of `/admin`; any one of them opens the view and its header link. Only a
 * `platform` scope counts: the server never publishes these actions elsewhere, and an answer on
 * another kind of scope must not open the platform view. A scope with no answer allows nothing.
 *
 * This decides what the NAVIGATION offers and which sections mount; it authorises nothing. The
 * server decides every platform read again and refuses anybody else with a `404`.
 */

import type { BackofficeScope, ScopePermission } from "../../../types/api";

/** The actions the server publishes on the platform scope, one per section of `/admin`. */
export const PLATFORM_ACTIONS = [
  "usage_totals:read",
  "usage_ceiling:manage",
  "system_status:read",
  "model_settings:manage",
  // Mockup v3 P3: Plataforma > Organizaciones (suspend and reactivate an organization).
  "organization:set_status",
] as const;

export type PlatformAction = (typeof PLATFORM_ACTIONS)[number] & ScopePermission;

/** Whether the server allows `action` over a platform scope the person holds. */
export function platformAllows(scopes: readonly BackofficeScope[], action: PlatformAction): boolean {
  return scopes.some((scope) => scope.kind === "platform" && (scope.permissions ?? []).includes(action));
}

/** Whether the platform view (and its header link) is offered: any platform action allowed. */
export function opensPlatformView(scopes: readonly BackofficeScope[]): boolean {
  return PLATFORM_ACTIONS.some((action) => platformAllows(scopes, action));
}
